<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <div class="column content-box">
      <woot-modal-header
        :header-title="$t('AGENT_MGMT.ADD.TITLE')"
        :header-content="$t('AGENT_MGMT.ADD.DESC')"
      />

      <form class="row" @submit.prevent="addAgent()">
        <div class="medium-12 columns">
          <input
            v-model.trim="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="$v.agentName.$touch"
          />
          <!-- agentName's error message  -->
          <span v-if="$v.agentName.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR') }}
          </span>
        </div>
        <div class="medium-12 columns">
          <label :class="{ error: $v.selectedRoleId.$error }">
            <select v-model="selectedRoleId" @change="$v.selectedRoleId.$touch">
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.label || role.name }}
              </option>
            </select>
            <span v-if="$v.selectedRoleId.$error" class="message">
              {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
            </span>
          </label>
        </div>
        <div class="medium-12 columns" style="margin-top: 1.5rem;">
          <input
            v-model.trim="agentEmail"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="$v.agentEmail.$touch"
          />
          <!-- agentEmail's error message  -->
          <span v-if="$v.agentEmail.$error" class="message">
            {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.ERROR') }}
          </span>
        </div>
        <div
          class="modal-footer"
          style="margin-bottom: -3rem;"
          :style="{ marginLeft: left }"
        >
          <div class="medium-12 columns" style="direction: rtl;">
            <woot-submit-button
              :disabled="
                $v.agentEmail.$invalid ||
                  $v.agentName.$invalid ||
                  uiFlags.isCreating
              "
              :button-text="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
              :loading="uiFlags.isCreating"
            />
            <button class="button clear" @click.prevent="onClose">
              {{ $t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { required, minLength, email } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
export default {
  components: {},
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      agentName: '',
      agentEmail: '',
      // selectedRoleId stores the selected role identifier (string for defaults, number for customs)
      selectedRoleId: 'agent',
      vertical: 'bottom',
      horizontal: 'center',
      show: true,
      left: this.$root.$i18n.locale === 'fr' ? '50rem' : '53.9rem',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'agents/getUIFlags',
      // map the custom roles getter from the customRoles module
      customRoles: 'customRoles/getCustomRoles',
    }),
    // build roles list combining default and custom roles
    roles() {
      const defaultRoles = [
        {
          id: 'administrator',
          name: 'administrator',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
          type: 'administrator',
        },
        {
          id: 'agent',
          name: 'agent',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.AGENT'),
          type: 'agent',
        },
      ];

      const custom = (this.customRoles || []).map(r => ({
        // keep the numeric id from backend but mark as custom via type
        id: r.id,
        name: `custom_${r.id}`,
        label: r.name,
        type: 'custom',
      }));

      return [...defaultRoles, ...custom];
    },
    selectedRole() {
      return this.roles.find(
        role =>
          String(role.id) === String(this.selectedRoleId) ||
          role.name === this.selectedRoleId
      );
    },
  },
  validations: {
    agentName: {
      required,
      minLength: minLength(1),
    },
    agentEmail: {
      required,
      email,
    },
    selectedRoleId: { required },
  },

  methods: {
    showAlert(message) {
      // keep existing bus usage for toasts
      if (typeof bus !== 'undefined' && bus && bus.$emit) {
        bus.$emit('newToastMessage', message);
      }
    },
    // selection handled by `selectedRoleId` v-model; no selectRole method needed
    async addAgent() {
      try {
        const payload = {
          name: this.agentName,
          email: this.agentEmail,
        };

        // If selected a custom role, send custom_role_id and set role to 'agent'.
        // If a default role is selected, set role accordingly and ensure custom_role_id is null.
        if (
          this.selectedRole &&
          String(this.selectedRole.name).startsWith('custom_')
        ) {
          payload.custom_role_id = Number(this.selectedRole.id);
          payload.role = 'agent';
        } else if (this.selectedRole) {
          payload.role = this.selectedRole.name;
          payload.custom_role_id = null;
        }

        await this.$store.dispatch('agents/create', payload);
        this.showAlert(this.$t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        const {
          response: { data: { error: errorResponse = '' } = {} } = {},
        } = error;
        let errorMessage = '';
        if (error.response && error.response.status === 422) {
          errorMessage = this.$t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
        } else {
          errorMessage = this.$t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
        }
        this.showAlert(errorResponse || errorMessage);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
/* error message's style */
.message {
  color: #f94b4a;
  color: var(--r-400);
  display: block;
  font-size: 1.4rem;
  font-size: var(--font-size-small);
  font-weight: 400;
  margin-bottom: 1rem;
  margin-top: -1.6rem;
  width: 100%;
}
</style>
